import React, { useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, Select, DatePicker, Checkbox } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Wrapper } from "../style";

dayjs.extend(customParseFormat); // to enable strict parsing with formats

const { Option } = Select;

function AddExpense({
                        isModalVisible,
                        handleCancel,
                        handleSubmit,
                        marketplaceList,
                        editingExpense,
                    }) {
    const [form] = Form.useForm();
    const [dateValue, setDateValue] = useState(null);

    const [categories, setCategories] = useState(["Category 1", "Category 2"]);
    const [newCategory, setNewCategory] = useState("");

    useEffect(() => {
        if (isModalVisible) {
            if (editingExpense && editingExpense.start_date) {
                const parsedDate = dayjs(editingExpense.start_date, "YYYY-MM-DD", true);
                if (parsedDate.isValid()) {
                    setDateValue(parsedDate);
                    form.setFieldsValue({
                        ...editingExpense,
                        start_date: parsedDate,
                    });
                } else {
                    setDateValue(null);
                    form.setFieldsValue({
                        ...editingExpense,
                        start_date: null,
                    });
                }
            } else {
                setDateValue(null);
                form.resetFields();
            }
        } else {
            setDateValue(null);
            form.resetFields();
        }
    }, [isModalVisible, editingExpense, form]);

    const onDateChange = (date) => {
        setDateValue(date ? date.startOf("day") : null);
        form.setFieldsValue({ start_date: date ? date.startOf("day") : null });
    };

    const handleAddCategory = () => {
        const trimmed = newCategory.trim();
        if (trimmed && !categories.includes(trimmed)) {
            setCategories([...categories, trimmed]);
            setNewCategory("");
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const processedValues = {
                ...values,
                start_date: values.start_date ? values.start_date.format("YYYY-MM-DD") : null,
                currency: 'INR',
                amortize_expense_daily: values.amortize_expense_daily ? 1 : 0,
                show_under_advertising_cost: values.show_under_advertising_cost ? 1 : 0,
            };
            if (editingExpense) {
                processedValues.id = editingExpense.id;
            }
            await handleSubmit(processedValues, editingExpense ? "edit" : "add");
            form.resetFields();
            setDateValue(null);
        } catch (error) {
            console.error("Validation Failed:", error);
        }
    };

    return (
        <Modal
            visible={isModalVisible}
            title={editingExpense ? "Edit Expense" : "Add Expense"}
            onCancel={handleCancel}
            width={600}
            destroyOnClose
            footer={[
                <>
                    <button type="button" className="btn btn-light me-3" onClick={handleCancel}>Close</button>
                    <button type="button" className="btn btn-danger" onClick={handleOk}>
                        {editingExpense ? "Update" : "Save"}
                    </button>
                </>
            ]}
        >
            <Wrapper>
                <div className="modal_custom">
                    <Form form={form} layout="vertical" name="expenseForm">
                        <div className="row g-5 mb-0 align-items-center">
                            <div className="col-md-8">
                                <Form.Item
                                    label=""
                                    name="name"
                                    className="mb-0"
                                    rules={[{ required: true, message: "Please enter an expense title" }]}
                                >
                                    <Input className="form-control form-control-solid fs-7" placeholder="Enter expense title" />
                                </Form.Item>
                            </div>
                            <div className="col-md-4">
                                <Form.Item
                                    label=""
                                    name="amount"
                                    className="mb-0"
                                    rules={[{ required: true, message: "Please enter an amount" }]}
                                >
                                    <InputNumber min={0} placeholder="Enter amount" style={{ width: "100%" }} />
                                </Form.Item>
                            </div>
                            <div className="col-md-4">
                                <Form.Item name="type" placeholder="Select Frequency" className="mb-0 min-h-40px">
                                    <Select placeholder="Select Frequency">
                                        <Option value="Daily">Daily</Option>
                                        <Option value="Weekly">Weekly</Option>
                                        <Option value="Monthly">Monthly</Option>
                                    </Select>
                                </Form.Item>
                            </div>
                            <div className="col-md-4">
                                <Form.Item className="mb-0" name="start_date">
                                    <DatePicker
                                        className="form-control form-control-solid fs-7 h-40px"
                                        format="YYYY-MM-DD"
                                        value={dateValue}
                                        onChange={onDateChange}
                                        allowClear
                                    />
                                </Form.Item>
                            </div>
                            <div className="col-md-4">
                                <Form.Item className="mb-0 min-h-40px" name="no_of_occurrences">
                                    <Select placeholder="Select Duration">
                                        <Option value="Once">Once</Option>
                                        <Option value="Forever">Forever</Option>
                                        <Option value=""># of occurrences</Option>
                                    </Select>
                                </Form.Item>
                            </div>
                            <div className="col-md-12">
                                <Form.Item name="amortize_expense_daily" className="mb-0" valuePropName="checked">
                                    <Checkbox>
                                        Amortize expense daily
                                    </Checkbox>
                                </Form.Item>
                            </div>
                            <div className="col-md-12">
                                <Form.Item name="show_under_advertising_cost" className="mb-0" valuePropName="checked">
                                    <Checkbox>
                                        Show this expense under "Advertising cost"
                                    </Checkbox>
                                </Form.Item>
                            </div>
                            <div className="col-md-6">
                                <Form.Item name="category">
                                    <Select
                                        placeholder="Select Category"
                                        dropdownRender={menu => (
                                            <>
                                                {menu}
                                                <div style={{ display: "flex", gap: 8, padding: 8 }}>
                                                    <Input
                                                        value={newCategory}
                                                        onChange={e => setNewCategory(e.target.value)}
                                                        placeholder="Add new category"
                                                        size="small"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        style={{ padding: "0 8px", fontSize: "12px" }}
                                                        onClick={handleAddCategory}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    >
                                        {categories.map(cat => (
                                            <Option key={cat} value={cat}>
                                                {cat}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </div>
                            <div className="col-md-6">
                                <Form.Item name="marketplace_ids">
                                    <Select
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.label.toLowerCase().includes(input.toLowerCase())
                                        }
                                        className=" fs-7"
                                        options={marketplaceList}
                                        allowClear
                                        placeholder="Select Marketplace"
                                        size="large"
                                    />
                                </Form.Item>
                            </div>
                            <div className="col-md-12">
                                <Form.Item name="products" className="position-relative mb-0">
                                    <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute " style={{ zIndex: 1, left: "11px", top: "10px" }}></i>
                                    <Input
                                        type="text"
                                        className="form-control form-control-solid text-dark fs-7 ps-12 all-search"
                                        placeholder="Search by sku, title or asin..."
                                    />
                                </Form.Item>
                            </div>
                        </div>
                    </Form>
                </div>
            </Wrapper>
        </Modal>
    );
}

export default AddExpense;